import { Component, Inject, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Domain, FRAUD_DOMAIN, SECURITY_DOMAIN } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";

import { QueryTrigger, TriggerType, TriggerTypeConfig } from "@app2/clients/types/alert-typedefs";
import { UBA_RISK_FACTORS } from "@app2/alert/alert-inbox/constants";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { FeaturesService } from "@app2/shared/services/features.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { triggerTypes } from "@app2/triggers/trigger-edit-page/constants";
import { determineTriggerType } from "@app2/triggers/utils";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { Observable } from "rxjs";

@Component({
    selector: "trigger-details",
    template: `
        <div class="trigger-details-container">
            <ng-container *ngIf="showTriggerDetails && currentTrigger.description">
                <div class="row query-info-container">
                    <span class="info-title medium-3 small-16"> {{ "DESCRIPTION" | localize }} </span>
                    <div class="formatted-html">
                        <span class="medium-13 small-16 columns" test-id="trigger-description-display"
                              [innerHtml]="currentTrigger.description | parseMarkup">
                        </span>
                    </div>
                </div>
            </ng-container>

            <ng-container *ngIf="showTriggerDetails && libraryDescription">
                <div class="row query-info-container" *ngIf="libraryDescription">
                    <span class="info-title medium-3 small-16"> {{ "LIBRARY_DESCRIPTION" | localize }} </span>
                    <div class="formatted-html">
                        <span class="medium-13 small-16 columns"
                              [innerHtml]="libraryDescription | parseMarkup">
                        </span>
                    </div>
                </div>
            </ng-container>

            <ng-container *ngIf="showTriggerDetails && currentTrigger.type === 'ueba'">
                <div class="row query-info-container">
                    <span class="info-title medium-3 small-16">
                        {{ "POSSIBLE_RISK_FACTORS" | localize }}
                    </span>
                    <div class="formatted-html">
                        <span class="medium-13 small-16 columns"> {{ "POSSIBLE_RISK_FACTORS_TEXT" | localize }}</span>
                    </div>
                </div>
            </ng-container>
            <ng-container *ngIf="showTriggerDetails && currentTrigger.type === 'fraud_uba'">
                <div class="row query-info-container">
                    <span class="info-title medium-3 small-16">
                        {{ "POSSIBLE_RISK_FACTORS" | localize }}
                    </span>
                    <span [class.hide-risk-factors]="!showAllRiskFactors" class="medium-13 small-16 columns">
                        <span *ngFor="let ubaRiskFactor of UBA_RISK_FACTORS">
                            <div class="risk-factor-title">
                                {{ ubaRiskFactor.titleKey | localize }}
                            </div>
                            <div class="risk-factor-description">
                                {{ ubaRiskFactor.description }}
                            </div>
                        </span>
                    </span>
                    <span class="medium-13 small-16 columns">
                         <ds-button type="blue-link"
                                    (clicked)="showAllRiskFactors = !showAllRiskFactors">
                                    {{ (!showAllRiskFactors ? "SEE_MORE_RISK_FACTORS" : "SEE_LESS_RISK_FACTORS") | localize }}
                             <i class="far"
                                [ngClass]="{'fa-chevron-up': showAllRiskFactors, 'fa-chevron-down': !showAllRiskFactors}">
                             </i>
                        </ds-button>
                    </span>
                </div>
            </ng-container>

            <!-- what about all the fake trigger types? -->
            <ng-container *ngIf="(productDomain === FRAUD_DOMAIN && showTriggerDetails) || productDomain === SECURITY_DOMAIN">
                <ng-container *ngIf="triggerConfiguration.name !== TriggerType.sequence
                                 && triggerConfiguration.name !== TriggerType.ueba
                                 && currentTrigger.savedQueryId
                                 && savedQuery$ | async as savedQuery">
                    <div class="row query-info-container">
                        <span class="info-title medium-3 small-16"> {{ "SEARCH_QUERY" | localize }} </span>
                        <span class="medium-13 small-16 columns">
                            <saved-query-summary [savedQueryId]="savedQuery.id">
                            </saved-query-summary>
                            <expand-events class="saved-query-actions"
                                           [queryString]="savedQuery.query"
                                           [showText]="'SHOW_MOST_RECENT_EVENTS'"
                                           [hideText]="'HIDE_MOST_RECENT_EVENTS'">
                            </expand-events>
                        </span>
                    </div>
                </ng-container>
            </ng-container>
            <div *ngIf="showTriggerDetails" class="trigger-type-container">
                <span class="info-title medium-3 small-16"> {{ "TRIGGER_TYPE" | localize }} </span>
                <div class="medium-13 small-16 columns">
                    <span>{{ triggerConfiguration.localizationKey | localize }}</span>
                    <ng-container *ngIf="realTriggerType === TriggerType.upperThreshold">
                        <span class="trigger-threshold-number"> {{ currentTrigger.maxMatch }}</span>
                        <i class="far fa-arrow-up trigger-threshold-icon"></i>
                    </ng-container>
                    <ng-container *ngIf="realTriggerType === TriggerType.lowerThreshold">
                        <span class="trigger-threshold-number"> {{ currentTrigger.minMatch }}</span>
                        <i class="far fa-arrow-down trigger-threshold-icon"></i>
                    </ng-container>
                    <div class="trigger-description">{{ triggerConfiguration.description }}</div>
                    <ng-container *ngIf="currentTrigger.intervalSeconds && currentTrigger.type !== 'ueba'">
                        <span class="trigger-detail-interval">{{"INTERVAL" | localize}}: </span>
                        <span class="trigger-detail-interval-time">
                            {{ currentTrigger.intervalSeconds | secondsToHumanRange }}
                        </span>
                    </ng-container>
                </div>
            </div>
            <ng-container *ngIf="showTriggerDetails && triggerConfiguration.name === TriggerType.sequence && (featureSeqTriggerSteps$ | async)">
                <div class="row query-info-container">
                    <span class="info-title medium-3 small-16">{{ "SEARCH_QUERY_SEQUENCE" | localize }}</span>
                    <div class="medium-13 small-16 columns">
                        <sequence-trigger-steps-info [steps]="currentTrigger.seqTriggerSteps"></sequence-trigger-steps-info>
                    </div>
                </div>
            </ng-container>
        </div>
        <div class="trigger-details-button-container">
            <ds-button type="blue-link"
                       (clicked)="showTriggerDetails = !showTriggerDetails"
                       test-id="trigger-details-button">
                {{ (!showTriggerDetails ? "SHOW_TRIGGER_DETAILS" : "HIDE_TRIGGER_DETAILS") | localize }}
                <i class="far" [ngClass]="{'fa-chevron-up': showTriggerDetails, 'fa-chevron-down': !showTriggerDetails}"></i>
            </ds-button>
        </div>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .trigger-details-container, .trigger-details-button-container {
            margin-top: 8px;
        }

        .info-title {
            color: $sub-header;
            font-size: 14px;
            float: left;
        }

        .query-info-container, .trigger-type-container {
            display: inline-block;
            width: 100%;
            padding-top: 8px;
            padding-bottom: 16px;
            border-bottom: 1px solid $gray-4;
        }

        .trigger-type-container {
            line-height: 16px;

            .trigger-description {
                margin: 4px 0;
                font-size: 14px;
                color: $gray-7;
            }

            .trigger-threshold-icon {
                font-size: 12px;
                margin-left: 4px;
            }

            .trigger-detail-interval, .trigger-detail-interval-time {
                font-size: 12px;
                color: $gray-7;
            }

            .trigger-threshold-number {
                font-weight: $font-weight-demibold;
            }

            .trigger-detail-interval-time {
                font-weight: $font-weight-bold;
            }
        }

        .risk-factor-title {
            color: $gray-7;
            font-size: 12px;
        }

        .risk-factor-description {
            color: $gray-9;
            margin-bottom: 4px;
            font-weight: $font-weight-demibold;
            line-height: 16px;
        }

        .hide-risk-factors {
            overflow-y: hidden;
            max-height: 128px;
        }
    `],
})
export class TriggerDetailsComponent implements OnChanges {
    TriggerType = TriggerType;
    FRAUD_DOMAIN = FRAUD_DOMAIN;
    SECURITY_DOMAIN = SECURITY_DOMAIN;
    UBA_RISK_FACTORS = UBA_RISK_FACTORS;

    @Input() currentTrigger: QueryTrigger;

    libraryDescription: string;
    featureSeqTriggerSteps$: Observable<boolean>;
    savedQuery$: Observable<SavedQuery>;
    triggerConfiguration: TriggerTypeConfig;
    // keeping real trigger type separate because other code still relies on "custom" instead of the actual type
    realTriggerType: TriggerType;
    showTriggerDetails: boolean = true;
    showAllRiskFactors: boolean = false;

    constructor(@Inject(PRODUCT_DOMAIN) public productDomain: Domain,
                private alertClient: AlertClientService,
                private notificationsService: NotificationsService,
                private savedQueryService: SavedQueryService,
                featuresService: FeaturesService) {
        this.featureSeqTriggerSteps$ = featuresService.getFeature$("sequenceTriggerAlertInbox");
        this.showTriggerDetails = this.productDomain === SECURITY_DOMAIN;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.currentTrigger && this.currentTrigger) {
            this.libraryDescription = "";
            this.savedQuery$ = this.savedQueryService.getSavedQuery$(this.currentTrigger.savedQueryId);

            this.realTriggerType = determineTriggerType(this.currentTrigger);
            this.triggerConfiguration = triggerTypes.find(config => config.name === this.realTriggerType);

            if (this.currentTrigger.defaultTriggerId) {
                this.alertClient.getDefaultTrigger(this.currentTrigger.defaultTriggerId)
                    .then(defaultTrigger => this.libraryDescription = defaultTrigger.description)
                    .catch(error => {
                        const message = getMessageForError(error.error, "FAILED_TO_RETRIEVE_TRIGGER");
                        this.notificationsService.showError(message, error);
                    });
            }
        }
    }
}
